<template>
  <div>
    <!-- Navigation Tabs -->
    <ul class="nav nav-tabs">
      <li v-for="(tab, index) in tabs" :key="index" class="nav-item">
        <a
          class="nav-link"
          :class="{ active: activeTab === index }"
          href="#"
          @click.prevent="selectTab(index)"
        >
          {{ tab.label }}
        </a>
      </li>
    </ul>

    <!-- Tab Content -->
    <div class="tab-content mt-3">
      <component
        :is="activeTabComponent"
        v-bind="getComponentProps(activeTabComponent)"
        @confirm="handleConfirm"
        @cancel="handleCancel"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useTestStore } from "../../../../stores/testStore";
import { interactions } from '../../../../config/interactions';
import { v4 as uuidv4 } from 'uuid';
import CreateComponent from "./CreateComponent.vue";
import AIGenerateComponent from "./AIGenerateComponent.vue";
import ImportComponent from "./ImportComponent.vue";

const store = useTestStore();

const props = defineProps({
  selectedElement: Object,
  isCatMode: {
    type: Boolean,
    default: false
  },
  forceOpen: {
    type: Boolean,
    default: false
  },
  // Nouvelles props pour support conditionnel
  context: {
    type: String,
    default: 'default' // 'default' | 'conditional'
  },
  testId: {
    type: String,
    default: ''
  },
  autoCreate: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['interaction-created', 'cancel', 'confirm']);

// Define tabs with their labels and components
const tabs = [
  { label: "Create", component: CreateComponent },
  { label: "AI Generate", component: AIGenerateComponent },
  { label: "Import", component: ImportComponent },
];

// Active tab index
const activeTab = ref(0);

// Computed property to determine the active component
const activeTabComponent = computed(() => tabs[activeTab.value].component);

// Function to provide props based on the component
const getComponentProps = (component) => {
  if (component === AIGenerateComponent) {
    return {
      prompt: "This is the AI prompt", // Example prompt
      selectedElement: props.selectedElement,
      context: props.context,
      testId: props.testId
    };
  }
  if (component === CreateComponent) {
    return {
      isCatMode: props.isCatMode,
      context: props.context,
      testId: props.testId,
      autoCreate: props.autoCreate,
      selectionMode: 'confirm'
    };
  }
  if (component === ImportComponent) {
    return {
      showItemBank: true
    };
  }
  return {}; // No props for other components
};

// Method to select a tab
function selectTab(index) {
  activeTab.value = index;
}

// Gérer la création d'interaction depuis CreateComponent
const handleConfirm = async (payload) => {
  console.log('📦 TabsComponent - Réception confirm:', payload);

  // Mode conditionnel : simplement transmettre au parent
  if (props.context === 'conditional') {
    console.log('🔄 Mode conditionnel - transmission au parent');
    emit('confirm', payload);
    return;
  }

  // Import JSON : gestion de l'importation multiple
  if (payload.action === 'import' && payload.items) {
    console.log('📥 Import JSON - Réception de', payload.items.length, 'items');

    try {
      const items = payload.items;

      // Récupérer l'élément actuellement sélectionné (l'élément "default" temporaire)
      const currentElement = store.selectedElement;

      if (currentElement && currentElement.el_Type === 'default') {
        // CAS 1: Remplacer l'élément default par le premier item importé
        console.log('🔄 Remplacement de l\'élément default par le premier item importé');

        const firstItem = items[0];

        // Trouver la page contenant l'élément default
        let currentPageID = null;
        if (store.testData.pages) {
          for (const page of store.testData.pages) {
            if (page.children && page.children.some(child => child.id === currentElement.el_ID)) {
              currentPageID = page.id;
              console.log(`📄 Élément default trouvé dans la page: ${currentPageID}`);
              break;
            }
          }
        }

        // Transformer l'élément default en conservant son ID
        const transformedElement = {
          ...firstItem, // Utiliser les données de l'item importé
          el_ID: currentElement.el_ID, // Conserver l'ID du default
        };

        console.log('✅ Élément default transformé:', transformedElement.el_Type, '-', transformedElement.el_Text);

        // Mettre à jour l'élément dans le store
        store.updateElement(transformedElement);

        // Mettre à jour le label dans la structure de page
        if (currentPageID && store.testData.pages) {
          const page = store.testData.pages.find(p => p.id === currentPageID);
          if (page) {
            const child = page.children.find(c => c.id === currentElement.el_ID);
            if (child) {
              child.label = transformedElement.el_Text;
            }
          }
        }

        // Re-sélectionner l'élément pour rafraîchir l'éditeur
        store.setSelectedElement(transformedElement, transformedElement.el_Text);

        // S'il y a plus d'un item, ajouter les autres dans la même page
        if (items.length > 1) {
          console.log(`📦 Ajout de ${items.length - 1} item(s) supplémentaire(s) dans la page ${currentPageID || 'racine'}`);
          for (let i = 1; i < items.length; i++) {
            const item = items[i];

            // FORCER un ID unique pour éviter les doublons
            item.el_ID = uuidv4();

            console.log(`📝 Ajout item ${i}/${items.length - 1}:`, item.el_Type, '-', item.el_Text, '- ID:', item.el_ID);

            // Ajouter avec le pageID pour qu'il apparaisse dans la structure
            store.addElementFromImport(item, currentPageID);

            console.log(`✅ Item ${i} ajouté - Total elements:`, store.testData.elements.length);
          }

          console.log('📊 Récapitulatif final:', store.testData.elements.map(e => `${e.el_Type}: ${e.el_Text}`));
        }

        emit('interaction-created', transformedElement);
      } else {
        // CAS 2: Pas d'élément default sélectionné, ajouter tous les items
        console.log('📦 Ajout de tous les items (pas de default à remplacer)');
        for (const item of items) {
          // S'assurer que l'item a un ID
          if (!item.el_ID) {
            item.el_ID = uuidv4();
          }

          store.addElementFromImport(item);
          console.log('✅ Item importé:', item.el_Type, '-', item.el_Text);
        }

        emit('interaction-created', { imported: items.length });
      }

      console.log('✅ Import JSON terminé -', items.length, 'items importés');
      alert(`${items.length} item(s) importé(s) avec succès !`);

    } catch (error) {
      console.error('❌ Erreur import JSON:', error);
      alert('Erreur lors de l\'import. Consultez la console pour plus de détails.');
    }
    return;
  }

  // Mode default : logique existante
  if (!payload.create) {
    console.warn('⚠️ Payload sans flag create, ignoré');
    return;
  }

  const { el_Type, label } = payload;

  try {
    // Récupérer l'élément actuellement sélectionné (l'élément "default")
    const currentElement = store.selectedElement;
    if (!currentElement) {
      console.error('❌ Aucun élément sélectionné');
      return;
    }

    console.log('🔄 Transformation de l\'élément default en', el_Type, '- ID:', currentElement.el_ID);

    // Charger le modèle de l'interaction
    let baseModel = null;
    if (interactions[el_Type]?.model) {
      const m = await interactions[el_Type].model();
      baseModel = JSON.parse(JSON.stringify(m));
    }

    // Transformer l'élément existant en conservant son ID
    const transformedElement = {
      ...currentElement, // Garder l'ID et autres propriétés existantes
      ...baseModel, // Appliquer le modèle de la nouvelle interaction
      el_ID: currentElement.el_ID, // S'assurer que l'ID ne change pas
      el_Type,
      el_Text: label || `New ${el_Type}`,
      el_Label: label || `New ${el_Type}`,
      difficulty_rating: currentElement.difficulty_rating ?? baseModel?.difficulty_rating ?? 5,
      metadata: {
        ...(currentElement.metadata || {}),
        ...(baseModel?.metadata || {}),
        source: 'CreateComponent',
        LastModif: new Date().toISOString()
      }
    };

    console.log('✅ Élément transformé:', transformedElement);

    // Mettre à jour le store avec l'élément transformé
    store.updateElement(transformedElement);

    // Re-sélectionner l'élément pour rafraîchir l'éditeur
    store.setSelectedElement(transformedElement, transformedElement.el_Label);

    emit('interaction-created', transformedElement);
  } catch (error) {
    console.error('❌ Erreur création interaction:', error);
  }
};

const handleCancel = () => {
  console.log('❌ Annulation création');
  emit('cancel');
};
</script>

<style scoped>
.nav-tabs .nav-link.active {
  font-weight: bold;
  border-color: #dee2e6 #dee2e6 #fff;
}
</style>
